import React, { useEffect } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { useParams, Navigate } from 'react-router-dom';
import { ChevronRight, Check, ArrowRight, Star, Shield, Zap, ExternalLink, BarChart, Database, Users, Code, Network, Lock, Brain, Lightbulb, LineChart, Link as LinkIcon } from 'lucide-react';
import { featuresData } from './FeaturesPage';
import './FeatureDetailPage.css';
import { Link } from 'react-router-dom';

// Feature-specific components with enhanced styling
const AISearchContent = () => (
  <div className="feature-specific-content">
    <motion.div 
      className="feature-section"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
    >
      <h3>Advanced Natural Language Processing</h3>
      <div className="feature-section-content">
        <div className="feature-section-text">
          <p>Our AI search technology understands the meaning behind your queries, not just the keywords. This allows for a more intuitive search experience that provides more accurate results.</p>
          <ul className="feature-list">
            <li>
              <span className="feature-highlight">Semantic understanding</span> that goes beyond keyword matching
            </li>
            <li>
              <span className="feature-highlight">Context preservation</span> across complex search queries
            </li>
            <li>
              <span className="feature-highlight">Entity recognition</span> to identify important concepts
            </li>
          </ul>
        </div>
        <div className="feature-section-image">
          <div className="feature-image-wrapper">
            <Brain size={60} className="feature-section-icon" />
          </div>
        </div>
      </div>
    </motion.div>
    
    <motion.div 
      className="feature-section alternate"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: 0.2 }}
    >
      <h3>Search Across Multiple Data Sources</h3>
      <div className="feature-section-content">
        <div className="feature-section-image">
          <div className="feature-image-wrapper">
            <Database size={60} className="feature-section-icon" />
          </div>
        </div>
        <div className="feature-section-text">
          <p>Connect all your enterprise systems for a unified search experience. Our technology works seamlessly across documents, databases, and applications.</p>
          <ul className="feature-list">
            <li>
              <span className="feature-highlight">Unified index</span> across all your data sources
            </li>
            <li>
              <span className="feature-highlight">Real-time synchronization</span> ensures up-to-date results
            </li>
            <li>
              <span className="feature-highlight">Format-agnostic</span> processing handles all document types
            </li>
          </ul>
        </div>
      </div>
    </motion.div>
  </div>
);

const DocumentManagementContent = () => (
  <div className="feature-specific-content">
    <motion.div 
      className="feature-section"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
    >
      <h3>Automated Document Understanding</h3>
      <div className="feature-section-content">
        <div className="feature-section-text">
          <p>Our system automatically analyzes documents to extract key information, identify themes, and make connections across your content.</p>
          <ul className="feature-list">
            <li>
              <span className="feature-highlight">Content extraction</span> from structured and unstructured documents
            </li>
            <li>
              <span className="feature-highlight">Named entity recognition</span> identifies key people, places, and concepts
            </li>
            <li>
              <span className="feature-highlight">Relationship mapping</span> between documents and concepts
            </li>
          </ul>
        </div>
        <div className="feature-section-image">
          <div className="feature-image-wrapper">
            <Code size={60} className="feature-section-icon" />
          </div>
        </div>
      </div>
    </motion.div>
    
    <motion.div 
      className="feature-section alternate"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: 0.2 }}
    >
      <h3>Visual Insights</h3>
      <div className="feature-section-content">
        <div className="feature-section-image">
          <div className="feature-image-wrapper">
            <LineChart size={60} className="feature-section-icon" />
          </div>
        </div>
        <div className="feature-section-text">
          <p>Transform your document data into visual insights with interactive graphs, charts, and knowledge maps that reveal patterns and connections.</p>
          <ul className="feature-list">
            <li>
              <span className="feature-highlight">Knowledge graphs</span> visualize connections between concepts
            </li>
            <li>
              <span className="feature-highlight">Topic clustering</span> groups related documents together
            </li>
            <li>
              <span className="feature-highlight">Trend analysis</span> tracks changes in topics over time
            </li>
          </ul>
        </div>
      </div>
    </motion.div>
  </div>
);

const BidAIContent = () => (
  <div className="feature-specific-content">
    <motion.div 
      className="feature-section"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
    >
      <h3>Automated RFP Response Generation</h3>
      <div className="feature-section-content">
        <div className="feature-section-text">
          <p>Transform your bidding process with AI that understands RFP requirements and generates winning responses by leveraging your past proposals and company knowledge.</p>
          <ul className="feature-list">
            <li>
              <span className="feature-highlight">Requirement extraction</span> automatically identifies key RFP requirements
            </li>
            <li>
              <span className="feature-highlight">Smart matching</span> finds relevant content from past proposals
            </li>
            <li>
              <span className="feature-highlight">Response generation</span> creates tailored, compliant responses
            </li>
          </ul>
        </div>
        <div className="feature-section-image">
          <div className="feature-image-wrapper">
            <Zap size={60} className="feature-section-icon" />
          </div>
        </div>
      </div>
    </motion.div>
    
    <motion.div 
      className="feature-section alternate"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: 0.2 }}
    >
      <h3>Contract Intelligence</h3>
      <div className="feature-section-content">
        <div className="feature-section-image">
          <div className="feature-image-wrapper">
            <Shield size={60} className="feature-section-icon" />
          </div>
        </div>
        <div className="feature-section-text">
          <p>Deep analysis of contracts to identify risks, obligations, and opportunities. Ensure compliance and protect your interests with AI-powered contract review.</p>
          <ul className="feature-list">
            <li>
              <span className="feature-highlight">Risk identification</span> flags potential issues and concerns
            </li>
            <li>
              <span className="feature-highlight">Obligation tracking</span> monitors deliverables and deadlines
            </li>
            <li>
              <span className="feature-highlight">Clause comparison</span> against your standard terms
            </li>
          </ul>
        </div>
      </div>
    </motion.div>
  </div>
);

const RecruitAIContent = () => (
  <div className="feature-specific-content">
    <motion.div 
      className="feature-section"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
    >
      <h3>Intelligent Resume Analysis</h3>
      <div className="feature-section-content">
        <div className="feature-section-text">
          <p>Our AI goes beyond keyword matching to truly understand candidate qualifications, experience, and potential fit for your roles.</p>
          <ul className="feature-list">
            <li>
              <span className="feature-highlight">Skills extraction</span> identifies both stated and implied competencies
            </li>
            <li>
              <span className="feature-highlight">Experience validation</span> verifies career progression and achievements
            </li>
            <li>
              <span className="feature-highlight">Qualification scoring</span> ranks candidates objectively
            </li>
          </ul>
        </div>
        <div className="feature-section-image">
          <div className="feature-image-wrapper">
            <Users size={60} className="feature-section-icon" />
          </div>
        </div>
      </div>
    </motion.div>
    
    <motion.div 
      className="feature-section alternate"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: 0.2 }}
    >
      <h3>Smart Matching Engine</h3>
      <div className="feature-section-content">
        <div className="feature-section-image">
          <div className="feature-image-wrapper">
            <Network size={60} className="feature-section-icon" />
          </div>
        </div>
        <div className="feature-section-text">
          <p>Match candidates to roles based on a comprehensive analysis of skills, experience, culture fit, and growth potential.</p>
          <ul className="feature-list">
            <li>
              <span className="feature-highlight">Multi-criteria matching</span> goes beyond simple keyword searches
            </li>
            <li>
              <span className="feature-highlight">Culture fit assessment</span> aligns values and work styles
            </li>
            <li>
              <span className="feature-highlight">Potential analysis</span> identifies candidates who can grow
            </li>
          </ul>
        </div>
      </div>
    </motion.div>
  </div>
);

// Component to render specific content based on feature slug
const FeatureSpecificContent = ({ slug }) => {
  switch(slug) {
    case 'ai-search-chat':
      return <AISearchContent />;
    case 'document-management':
      return <DocumentManagementContent />;
    case 'bidai-contract-analysis':
      return <BidAIContent />;
    case 'recruitai-talent':
      return <RecruitAIContent />;
    case 'enterprise-security':
      return (
        <div className="feature-specific-content">
          <motion.div 
            className="feature-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h3>Enterprise-Grade Security</h3>
            <div className="feature-section-content">
              <div className="feature-section-text">
                <p>Security is built into every layer of our platform, ensuring your sensitive data remains protected while enabling powerful AI capabilities.</p>
                <ul className="feature-list">
                  <li>
                    <span className="feature-highlight">End-to-end encryption</span> for all data
                  </li>
                  <li>
                    <span className="feature-highlight">Role-based access control</span> with granular permissions
                  </li>
                  <li>
                    <span className="feature-highlight">Audit logging</span> for all system activities
                  </li>
                </ul>
              </div>
              <div className="feature-section-image">
                <div className="feature-image-wrapper">
                  <Lock size={60} className="feature-section-icon" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      );
    default:
      return <AISearchContent />;
  }
};

const FeatureDetailPage = () => {
    const { slug } = useParams();
    const feature = featuresData.find(f => f.slug === slug);
    const controls = useAnimation();

    useEffect(() => {
        window.scrollTo(0, 0);
        
        controls.start(i => ({
            opacity: 1,
            y: 0,
            transition: {
                delay: i * 0.1,
                duration: 0.5,
                ease: "easeOut"
            }
        }));
    }, [controls, slug]);

    if (!feature) {
        return <Navigate to="/features" replace />;
    }

    const Icon = feature.icon;

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5
            }
        }
    };

    // Use the actual key points from the feature data
    const featureCards = feature.detailContent?.keyPoints || [
        {
            icon: Star,
            title: "Key Feature 1",
            description: "Description of the first key feature.",
            benefits: feature.shortItems.slice(0, 2)
        },
        {
            icon: Shield,
            title: "Key Feature 2",
            description: "Description of the second key feature.",
            benefits: feature.shortItems.slice(2, 4)
        }
    ];

    return (
        <div className="feature-detail-page">
            {/* Hero Section */}
            <motion.div
                className="feature-detail-hero"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                <div className="feature-detail-hero-content">
                    <motion.div
                        className="feature-detail-icon-wrapper"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ 
                            delay: 0.2,
                            type: "spring",
                            stiffness: 100
                        }}
                    >
                        <Icon className="feature-detail-icon" />
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                    >
                        {feature.title}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        className="feature-detail-description"
                    >
                        {feature.description}
                    </motion.p>
                    <motion.div
                        className="feature-detail-cta"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.6 }}
                    >
                        <Link to='/contact' className="primary-button">
                            Schedule Consultation
                            <ArrowRight size={20} />
                        </Link>
                    </motion.div>
                </div>
            </motion.div>

            {/* Feature-Specific Content */}
            <section className="feature-specific-section">
                <div className="container">
                    <FeatureSpecificContent slug={slug} />
                </div>
            </section>

            {/* Key Points Section */}
            <section className="feature-detail-keypoints">
                <div className="container">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        Key Benefits
                    </motion.h2>
                    <motion.div
                        className="keypoints-grid"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {featureCards.map((card, index) => (
                            <motion.div
                                key={index}
                                className="keypoint-card"
                                variants={itemVariants}
                                custom={index}
                                whileHover={{ 
                                    scale: 1.01,
                                    transition: { duration: 0.2 }
                                }}
                            >
                                <div className="keypoint-header">
                                    {card.icon && <card.icon className="feature-icon" />}
                                    <h3>{card.title}</h3>
                                </div>
                                <p>{card.description}</p>
                                <ul className="benefits-list">
                                    {card.benefits && card.benefits.map((benefit, idx) => (
                                        <motion.li
                                            key={idx}
                                            initial={{ opacity: 0, x: -10 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: idx * 0.1 }}
                                        >
                                            <Check size={16} />
                                            <span>{benefit}</span>
                                        </motion.li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Overview Section */}
            <section className="feature-detail-overview">
                <div className="container">
                    <div className="overview-content">
                        <motion.div
                            className="overview-text"
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2>Transform Your Business</h2>
                            <p className="overview-description">
                                {feature.detailContent?.overview || `Unlock the full potential of your organization with ${feature.title}. Our advanced AI capabilities help you work smarter, faster, and more efficiently than ever before.`}
                            </p>
                        </motion.div>
                        <motion.div
                            className="overview-visual"
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <motion.div 
                                className="overview-icon"
                                animate={{ 
                                    rotate: [0, 5, 0, -5, 0],
                                }}
                                transition={{
                                    duration: 6,
                                    ease: "easeInOut",
                                    repeat: Infinity,
                                }}
                            >
                                <Icon size={120} />
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="feature-detail-cta-section">
                <div className="container">
                    <motion.div
                        className="cta-content"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2>Ready to Get Started?</h2>
                        <p>Join hundreds of companies already using {feature.title} to transform their operations and drive growth.</p>
                        <div className="cta-buttons">
                            <Link to='/contact' className="secondary-button">
                                Contact Sales
                                <ExternalLink size={18} />
                            </Link>
                            <Link to='/demo' className="secondary-button">
                                Schedule Demo
                                <ChevronRight size={18} />
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default FeatureDetailPage;